import { store } from '@/store';
import {
  setIntegrationData,
  setIntegrationDataFetched,
  setIntegrationError,
  setIntegrationLoading,
} from '@/store/slices/integrationSlice';
import { integrationService } from './integration-service';

/**
 * Centralized Integration Data Service
 *
 * This service manages all integration data fetching and prevents duplicate API calls.
 * It uses Redux store to track what data has been fetched and when.
 */
export class IntegrationDataService {
  private static instance: IntegrationDataService;
  private isFetching = false;

  private constructor() {}

  static getInstance(): IntegrationDataService {
    if (!IntegrationDataService.instance) {
      IntegrationDataService.instance = new IntegrationDataService();
    }
    return IntegrationDataService.instance;
  }

  /**
   * Fetch integration data if not already fetched
   * This is the main method that should be called to get integration data
   */
  async fetchIntegrationDataIfNeeded(partnerId: string): Promise<{
    success: boolean;
    partner?: any;
    endpoints?: any[];
    error?: string;
  }> {
    const state = store.getState();
    const { dataFetched, partner, endpoints } = state.integration;

    // If we already have the data and it's for the same partner, return it
    if (dataFetched && partner && partner.id === partnerId) {
      console.log(
        '✅ IntegrationDataService: Using existing data from Redux store'
      );
      return {
        success: true,
        partner: partner,
        endpoints: endpoints,
      };
    }

    // If we're already fetching, wait for the current fetch to complete
    if (this.isFetching) {
      console.log('⏳ IntegrationDataService: Already fetching, waiting...');
      // Wait for the fetch to complete
      while (this.isFetching) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Check again after waiting
      const newState = store.getState();
      if (
        newState.integration.dataFetched &&
        newState.integration.partner?.id === partnerId
      ) {
        console.log(
          '✅ IntegrationDataService: Data fetched by another process'
        );
        return {
          success: true,
          partner: newState.integration.partner,
          endpoints: newState.integration.endpoints,
        };
      }
    }

    // Fetch the data
    return this.fetchIntegrationData(partnerId);
  }

  /**
   * Force fetch integration data (ignores cache)
   */
  async forceFetchIntegrationData(partnerId: string): Promise<{
    success: boolean;
    partner?: any;
    endpoints?: any[];
    error?: string;
  }> {
    console.log(
      '🔄 IntegrationDataService: Force fetching integration data...'
    );
    return this.fetchIntegrationData(partnerId);
  }

  /**
   * Internal method to fetch integration data
   */
  private async fetchIntegrationData(partnerId: string): Promise<{
    success: boolean;
    partner?: any;
    endpoints?: any[];
    error?: string;
  }> {
    if (this.isFetching) {
      return {
        success: false,
        error: 'Already fetching integration data',
      };
    }

    this.isFetching = true;
    store.dispatch(setIntegrationLoading(true));
    console.log(
      '🔄 IntegrationDataService: Fetching integration data for partner:',
      partnerId
    );

    try {
      const result = await integrationService.fetchIntegrationPartnerById(
        partnerId
      );

      if (result.success && result.partner) {
        // Store in Redux using the new action
        store.dispatch(
          setIntegrationData({
            partner: result.partner,
            endpoints: result.partner.endpoints,
          })
        );
        store.dispatch(setIntegrationDataFetched(true));

        console.log(
          '✅ IntegrationDataService: Integration data fetched and stored'
        );
        console.log('📋 Partner:', result.partner.name);
        console.log('📋 Endpoints:', result.partner.endpoints.length);

        return {
          success: true,
          partner: result.partner,
          endpoints: result.partner.endpoints,
        };
      } else {
        console.error(
          '❌ IntegrationDataService: Failed to fetch integration data:',
          result.error
        );
        store.dispatch(
          setIntegrationError(
            result.error || 'Failed to fetch integration data'
          )
        );
        return {
          success: false,
          error: result.error || 'Failed to fetch integration data',
        };
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      console.error(
        '❌ IntegrationDataService: Error fetching integration data:',
        errorMessage
      );
      store.dispatch(setIntegrationError(errorMessage));
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      this.isFetching = false;
      store.dispatch(setIntegrationLoading(false));
    }
  }

  /**
   * Clear integration data from Redux store
   */
  clearIntegrationData(): void {
    store.dispatch(
      setIntegrationData({
        partner: null,
        endpoints: [],
      })
    );
    store.dispatch(setIntegrationDataFetched(false));
    console.log(
      '🗑️ IntegrationDataService: Cleared integration data from Redux'
    );
  }

  /**
   * Get current integration data from Redux store
   */
  getCurrentIntegrationData(): {
    partner: any;
    endpoints: any[];
    isFetched: boolean;
  } {
    const state = store.getState();
    return {
      partner: state.integration.partner,
      endpoints: state.integration.endpoints,
      isFetched: state.integration.dataFetched,
    };
  }
}

// Export singleton instance
export const integrationDataService = IntegrationDataService.getInstance();
